import {
  ClientWorkFlowStatus,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../modules/BulkActions";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { PayablesTableItemData } from "./PayablesTable";

function processUnapprovePayables(
  data: PayablesTableItemData[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> {
  const bulkAction = bulkActionFactory<PayablesTableItemData>({
    onProgress,
    action: async item => {
      const payableId = item.payable?.payableId || "";

      if (item.payable?.status === InvoiceStatus.Paid) {
        throw new Error("Cannot unapprove paid payable");
      }

      return await paymentsService.payable.update(payableId, {
        client: { workflowStatus: ClientWorkFlowStatus.Pending }
      });
    },
    onFinish: ({ successItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });
      onSuccess?.();
    }
  });

  return bulkAction.run(data).then(({ successItems, failedItems }) => ({
    total: data.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
}

export function useBulkUnapprovePayables() {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayablesTableItemData[], onSuccess?: () => void) =>
    runBulkAction({
      actionName: "Unapprove payables",
      itemCount: items.length,
      confirmDescription: "Unapprove payables from being paid",
      confirmLabel: "Unapprove",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processUnapprovePayables(data, onProgress, onSuccess)
    });
}
